import { useState } from 'react'
import { IoMdLink } from 'react-icons/io'
import { PiBuildings } from 'react-icons/pi'
import { cn } from 'components/UI/cn'

interface CompanyHeaderProps {
  companyName: string
  logoUrl?: string
  domain?: string
  isUserCreated?: boolean
}

// Aspect ratio above which a logo is treated as a wide wordmark
// (e.g. "Dunkin'", "FedEx") and displayed at natural proportions
// instead of cropped into a circular avatar.
const WIDE_LOGO_ASPECT_THRESHOLD = 1.5

export function CompanyHeader({
  companyName,
  logoUrl,
  domain,
  isUserCreated,
}: CompanyHeaderProps) {
  const [isWideLogo, setIsWideLogo] = useState(false)

  return (
    <div className="flex flex-col items-center">
      <div className="flex flex-col items-center gap-2.5 self-stretch mt-3">
        <div className="flex flex-col items-center gap-2.5">
          {/* Company Logo (skipped for user-created entries; the Custom Company banner at the top of the panel covers that case) */}
          {!isUserCreated && (
            <>
              {logoUrl ? (
                <img
                  src={logoUrl}
                  alt={`${companyName} logo`}
                  onLoad={(e) => {
                    const img = e.currentTarget
                    setIsWideLogo(
                      img.naturalWidth / img.naturalHeight >
                        WIDE_LOGO_ASPECT_THRESHOLD
                    )
                  }}
                  className={cn(
                    isWideLogo
                      ? 'max-w-[200px] h-12 object-contain'
                      : 'w-12 h-12 rounded-full mr-1 object-cover object-center'
                  )}
                />
              ) : (
                <div className="w-12 h-12 min-w-12 min-h-12 rounded-full bg-gray-200 flex items-center justify-center">
                  <PiBuildings className="w-6 h-6 text-gray-700" />
                </div>
              )}
            </>
          )}

          {/* Company Name */}
          <div
            className={cn(
              'font-semibold text-3xl text-center text-gray-900 leading-tight',
              { 'mt-2': isUserCreated }
            )}
          >
            {companyName}
          </div>
        </div>

        {/* Domain Badge */}
        {domain && (
          <div className="flex items-center gap-3">
            <a
              href={`https://${domain}`}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1 px-2 py-1 border border-gray-200 rounded-lg bg-white hover:bg-gray-50 transition-colors"
            >
              <IoMdLink className="w-4 h-4 text-brand-bright" />
              <span className="text-xs font-medium text-brand-bright text-center">
                {domain}
              </span>
            </a>
          </div>
        )}
      </div>
    </div>
  )
}
